import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import api from "../../api";

export default function GoogleCalenderCallback() {
  const brand = useMemo(
    () => ({
      teal: "#55c5d0",
      border: "rgb(229,231,235)",
      bg: "rgb(248,250,252)",
    }),
    []
  );

  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  // Use the configured API instance instead of manual axios calls
  // This ensures proper CORS handling and authentication

  const qp = (key) => {
    const q = new URLSearchParams(location.search).get(key);
    if (q) return q;
    if (location.hash?.startsWith("#")) {
      const h = new URLSearchParams(location.hash.slice(1)).get(key);
      if (h) return h;
    }
    return null;
  };

  useEffect(() => {
    let code = qp("code") || "";
    const scope = qp("scope") || "";
    const error = qp("error");

    if (error) {
      setErrorMsg(`Google returned an error: ${error}`);
      setLoading(false);
      return;
    }
    if (!code) {
      setErrorMsg("Missing authorization code in callback URL.");
      setLoading(false);
      return;
    }

    // Pack scope inside code as “%26scope=…”, only if it's not already embedded
    if (scope && !code.includes("%26scope=")) {
      code = `${code}%26scope=${scope}`;
    }

    (async () => {
      try {
        // `state` is the signed value the CRM put in the consent link; the
        // backend checks it so a forged callback can't connect someone else's calendar.
        await api.post(`google/calendar/oauth/callback/`, { code, state: qp("state") || "" }, { timeout: 30000 });

        // tell the list page we’re connected
        sessionStorage.setItem("google_calendar_connected", "1");

        // 🔙 stay on the same feature: go back to /google-calender and show the datepicker UI
        navigate("/google-calender?connected=1");
      } catch (e) {
        const detail =
          e?.response?.data?.detail ||
          e?.response?.data?.message ||
          "Failed to finalize Google Calendar connection. Please try again.";
        setErrorMsg(detail);
      } finally {
        setLoading(false);
      }
    })();
  }, [location.search, location.hash, navigate]);

  return (
    <div className="min-h-screen" style={{ background: brand.bg }}>
      <main className="max-w-screen-md mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div
          className="bg-white rounded-2xl shadow-sm border p-6 sm:p-8"
          style={{ borderColor: brand.border }}
        >
          <h1 className="text-2xl font-semibold text-slate-900">
            Connecting Google Calendar…
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            Please wait while we complete the connection.
          </p>

          {loading && (
            <div className="mt-6 inline-flex items-center gap-2 text-slate-700">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4A4 4 0 008 12H4z"
                />
              </svg>
              Finalizing with Google…
            </div>
          )}

          {!loading && errorMsg && (
            <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-800 text-sm">
              <p className="font-medium mb-1">Couldn’t connect.</p>
              <p className="whitespace-pre-wrap break-words">{errorMsg}</p>
              <div className="mt-4 flex items-center gap-2">
                <button
                  onClick={() => (window.location.href = "/google-calender")}
                  className="px-4 py-2.5 rounded-lg text-white font-medium shadow-sm"
                  style={{ backgroundColor: brand.teal }}
                >
                  Try Again
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2.5 rounded-lg border"
                  style={{ borderColor: brand.border }}
                >
                  Reload Page
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
